import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import * as wjcXlsx from '@mescius/wijmo.xlsx'
import { Popover } from '@material-ui/core'
import InputIcon from '@material-ui/icons/Input'
import { makeStyles } from '@material-ui/core/styles'
import Encoding from 'encoding-japanese'

const useStyles = makeStyles((theme) => ({
  popover: {
    pointerEvents: 'none'
  },
  paper: {
    padding: theme.spacing(1)
  }
}))

export default function FlexGridFileImport (props) {
  const classes = useStyles()
  const { tableFormat, collectionView, className } = props
  const [anchorEl, setAnchorEl] = useState(null)
  const fileInput = useRef()

  // アイテムデータの配列rowsを受け取ってCollectionViewに追加する
  function setCollectionView (rows) {
    rows.forEach((row) => {
      collectionView.sourceCollection.push(row)
    })
    collectionView.collectionChanged.raise(collectionView)
    collectionView.sourceCollectionChanged.raise(collectionView)
  }

  // - fileRows: そのファイル形式上の行の配列
  // - getValuesFromRow: 行から値の配列に変換するコールバック
  // を受け取り、setCollectionViewに渡す配列rowsを返す
  function fileToRows (fileRows, getValuesFromRow) {
    const textToCol = new Map(tableFormat.filter((col) => col.isEditable).map((col) => [col.text, col]))
    const indexToCol = new Map()
    const rows = []
    fileRows.forEach((fileRow, i) => {
      const values = getValuesFromRow(fileRow)
      if (i === 0) {
        // ヘッダーから読み取る列と値のマップを作成する
        values.forEach((value, j) => {
          if (textToCol.has(value)) {
            indexToCol.set(j, textToCol.get(value))
          }
        })
      } else {
        const row = {}
        // 初期値を格納
        textToCol.forEach((col, text) => {
          if (col.type === 'number') {
            row[col.field] = 0
          } else {
            row[col.field] = ''
          }
        })
        values.forEach((value, j) => {
          if (indexToCol.has(j)) {
            const col = indexToCol.get(j)
            if (col.type === 'number') {
              if (col.isRate) {
                const v = value?.toString() ?? ''
                // isRateのときはmaxLengthは%表示のときの小数点以下の桁数と解釈する
                const p = Math.pow(10, col.maxLength)
                const num = Math.round(Number(v) * p) / p
                row[col.field] = num
              } else {
                const v = value?.toString().substring(0, col.maxLength) ?? ''
                row[col.field] = Number(v)
              }
            } else {
              const v = value?.toString().substring(0, col.maxLength) ?? ''
              row[col.field] = v
            }
          }
        })
        rows.push(row)
      }
    })
    return rows
  }

  // csvのFileDataを受け取ってCollectionViewにインポートする
  function importCsv (file) {
    const reader = new FileReader()
    reader.onload = function () {
      let codes = new Uint8Array(reader.result)
      const encoding = Encoding.detect(codes)

      // UTF-8の場合、先頭がBOM (EF BB BF)であるかチェックし、BOMなら削除する
      if (encoding === 'UTF8' && codes.length > 3 && codes[0] === 239 && codes[1] === 187 && codes[2] === 191) {
        codes = codes.slice(3)
      }

      const encodedContents = Encoding.convert(codes, {
        to: 'unicode',
        from: encoding,
        type: 'string'
      }).replaceAll('\t', ',')

      const rows = fileToRows($.csv.toArrays(encodedContents), (row) => row)
      setCollectionView(rows)
    }

    reader.readAsArrayBuffer(file)
  }

  // xlsxのFileDataを受け取ってCollectionViewにインポートする
  function importXlsx (file) {
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      const workbook = new wjcXlsx.Workbook()
      workbook.loadAsync(reader.result, (result) => {
        if (result.sheets.length === 0) return
        const sheet = result.sheets[0]
        const rows = fileToRows(sheet.rows, (row) => row.cells.map((cell) => cell.value))
        setCollectionView(rows)
      })
    })
    reader.readAsDataURL(file)
  }

  // input:file要素に渡すchangeイベントのハンドラ
  function onChange (e) {
    const files = e.target.files
    if (files && files.length > 0) {
      const file = files[0]
      const ext = file.name.split('.').pop()
      if (ext === 'csv') {
        importCsv(file)
      } else if (ext === 'xlsx') {
        importXlsx(file)
      }
    }
  }

  return (
    <>
      <label htmlFor="import-file" className={`btn ol-theme-success ${className}`}
        onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
        onMouseLeave={(e) => setAnchorEl(null)}
        >
        <InputIcon />
        インポート
      </label>
      {/* formからsubmitしないためinput:fileにname属性は設定しない */}
      <input type="file" id="import-file"
        className="d-none" ref={fileInput} onChange={onChange}
        accept=".csv,.xlsx"
      />
      <Popover
        className={classes.popover}
        classes={{
          paper: classes.paper
        }}
        open={!!anchorEl}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        onClose={() => setAnchorEl(null)}
        disableRestoreFocus
        >
        <div>
          インポートのフォーマットは以下の手順でダウンロードいただけます。
          <ol>
            <li>下の入力グリッドを右クリック</li>
            <li>「エクスポート」を選択</li>
          </ol>
        </div>
      </Popover>
    </>
  )
}

FlexGridFileImport.propTypes = {
  tableFormat: PropTypes.arrayOf(PropTypes.object),
  collectionView: PropTypes.object,
  className: PropTypes.string
}
